import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://static.readdy.ai/image/1e1522e14c640395ed26d10e936fd941/2c36f21ce67591e9de99c399e19fe8d5.png';

const navItems = [
  {
    label: 'Insurance Solutions',
    href: '/insurance-solutions',
    children: [
      { label: 'Personal Insurance', href: '/insurance-solutions#personal' },
      { label: 'Business Insurance', href: '/insurance-solutions#business' },
      { label: 'Insurance by Industry', href: '/insurance-solutions#industry' },
    ],
  },
  {
    label: 'Service Center',
    href: '/service-center',
    children: [
      { label: 'Report a Claim', href: '/service-center#claim' },
      { label: 'Request Auto ID Card', href: '/service-center#auto-id' },
      { label: 'Request a Certificate', href: '/service-center#certificate' },
      { label: 'Request Policy Change', href: '/service-center#policy-change' },
    ],
  },
  {
    label: 'About',
    href: '/about',
    children: [
      { label: 'About Our Agency', href: '/about#agency' },
      { label: 'Meet Our Team', href: '/about#team' },
      { label: 'Read Our Reviews', href: '/about#reviews' },
      { label: 'We Are Independent', href: '/about#independent' },
    ],
  },
  { label: 'Contact', href: '/contact', children: [] },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHome ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Insurance Agency Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative group"
                onMouseEnter={() => setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`px-4 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 whitespace-nowrap rounded-md ${
                    scrolled || !isHome
                      ? 'text-[#0b2240] hover:text-[#3072b7]'
                      : 'text-white hover:text-[#9cbde5]'
                  }`}
                >
                  {item.label}
                  {item.children.length > 0 && (
                    <i className="ri-arrow-down-s-line ml-1 text-xs" />
                  )}
                </Link>
                {item.children.length > 0 && openDropdown === item.label && (
                  <div className="absolute top-full left-0 mt-0 w-56 bg-white shadow-lg rounded-b-lg border-t-2 border-[#3072b7] py-2 z-50">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className="block px-5 py-2.5 text-sm text-[#0b2240] hover:bg-[#9cbde5]/20 hover:text-[#3072b7] transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:6013259000"
              className={`text-sm font-bold whitespace-nowrap transition-colors ${
                scrolled || !isHome ? 'text-[#0b2240]' : 'text-white'
              }`}
            >
              <i className="ri-phone-line mr-1" />
              601-325-9000
            </a>
            <Link
              to="/contact"
              className="bg-[#3072b7] hover:bg-[#0b2240] text-white text-sm font-bold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap"
            >
              Request Quote
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${scrolled || !isHome ? 'text-[#0b2240]' : 'text-white'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white shadow-lg border-t border-gray-100">
            <div className="px-4 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <div key={item.label}>
                  <button
                    className="w-full text-left px-4 py-3 text-sm font-semibold text-[#0b2240] hover:text-[#3072b7] flex items-center justify-between cursor-pointer"
                    onClick={() =>
                      setOpenDropdown(openDropdown === item.label ? null : item.label)
                    }
                  >
                    {item.label}
                    {item.children.length > 0 && openDropdown === item.label && (
                      <i className="ri-arrow-up-s-line" />
                    )}
                    {item.children.length > 0 && openDropdown !== item.label && (
                      <i className="ri-arrow-down-s-line" />
                    )}
                  </button>
                  {item.children.length > 0 && openDropdown === item.label && (
                    <div className="pl-6 flex flex-col gap-1 mb-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-[#3072b7] hover:text-[#0b2240]"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-3 border-t pt-3 flex flex-col gap-3">
                <a href="tel:6013259000" className="flex items-center gap-2 text-sm font-bold text-[#0b2240] px-4">
                  <i className="ri-phone-line" /> 601-325-9000
                </a>
                <Link
                  to="/contact"
                  className="mx-4 bg-[#3072b7] text-white text-sm font-bold px-5 py-3 rounded-md text-center"
                >
                  Request Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
